import { useCallback, useEffect, useRef, useState } from 'react'
import { auth } from '../firebase/app'
import authService from './index'
import { fetchNavigation } from './navigationRequest'

const idle = { status: 'loading', data: null }

function messageFor(error) {
  switch (error.response?.status) {
    case 401: return 'Tu sesión venció. Volvé a iniciar sesión.'
    case 403: return 'Tu perfil no tiene acceso a la navegación.'
    case 404: return 'No se encontró la navegación para tu perfil.'
    default: return 'No se pudo cargar el menú.'
  }
}

export default function useNavigation(enabled, token, profileRefresh) {
  const [result, setResult] = useState(null)
  const [revision, setRevision] = useState(0)
  const renewal = useRef(null)
  const version = authService.sessionVersion
  const scope = enabled ? `${version}:${token}:${profileRefresh}` : null
  const retry = useCallback(() => {
    renewal.current = null
    setResult(null)
    setRevision((value) => value + 1)
  }, [])

  useEffect(() => {
    if (!scope) {
      setResult(null)
      return
    }

    const controller = new AbortController()
    const firebaseUser = auth.currentUser
    const isCurrent = () => !controller.signal.aborted && authService.sessionVersion === version &&
      auth.currentUser === firebaseUser && authService.state.user.token === token

    const publish = (value) => {
      if (isCurrent()) setResult({ ...value, scope })
    }

    async function load() {
      try {
        let data
        try {
          data = await fetchNavigation(token, controller.signal)
        } catch (error) {
          if (!isCurrent() || error.response?.status !== 401 || !firebaseUser || renewal.current === version) throw error
          renewal.current = version
          let renewedToken
          try {
            renewedToken = await firebaseUser.getIdToken(true)
          } catch {
            throw error
          }
          if (!isCurrent()) return
          if (renewedToken && renewedToken !== token) {
            // The token observer may already have started the next request.
            authService.setToken(renewedToken)
            return
          }
          data = await fetchNavigation(token, controller.signal)
        }
        if (isCurrent()) renewal.current = null
        publish({ status: 'ready', data })
      } catch (error) {
        publish({ status: 'error', data: null, message: messageFor(error), httpStatus: error.response?.status })
      }
    }

    load()
    return () => controller.abort()
  }, [scope, token, revision, version])

  useEffect(() => {
    if (!scope) return
    const refreshOnFocus = () => setRevision((value) => value + 1)
    window.addEventListener('focus', refreshOnFocus)
    return () => window.removeEventListener('focus', refreshOnFocus)
  }, [scope])

  const current = scope && result?.scope === scope ? result : idle
  return { ...current, retry }
}
